import { useMemo, useState } from 'react';
import {
  Box, Formblock, Select, SearchBar, FormSection,
  Button,
} from '@zolteam/zol-admin-ui';
import { useTranslation } from 'react-i18next';
// Services
import { useGetCustomerListQuery } from '../../../services/customers/customersQueries';
import { useGetAdminListQuery } from '../../../services/users/usersQueries';
// Domain
import { CUSTOMER_STATUS, getFullname } from '../../../domain/customers/customersUseCases';

/**
 * CustomerFilters component.
 * @param {object} props
 * @param {URLSearchParams} props.searchParam
 * @param {Function} props.handleSearchParamChange
 * @param {string} props.customerFilter
 * @param {string} props.statusFilter
 * @param {string} props.brokerIds
 * @returns {JSX.Element}
 */
function CustomerFilters({
  searchParam,
  handleSearchParamChange,
  customerFilter,
  statusFilter,
  brokerIds,
}) {
  const { t } = useTranslation();
  const [currentSearch, setCurrentSearch] = useState('');

  const { data: customers } = useGetCustomerListQuery(
    {
      pageNumber: 1,
      pageSize: 10,
      search: currentSearch,
    },
    {
      keepPreviousData: false,
    },
  );

  const customerOptions = useMemo(() => ([{
    label: t('layout.menu.entries.customers'),
    options: customers?.data?.map(({ firstname, lastname, email }) => ({
      label: `${firstname} ${lastname}`,
      value: email,
    })) || [],
  }]), [customers?.data]);

  const { data: admins } = useGetAdminListQuery({
    pageNumber: 1,
    pageSize: 100,
  });

  const adminOptions = useMemo(() => admins?.data?.map(
    /**
     * @param {User} admin
     * @returns {{label: string | number, value: number | string}}
     */
    ({ firstname, lastname, id }) => ({ label: `${firstname} ${lastname}`, value: id }),
  ), [admins]);

  /**
   * Handle the customers search bar change.
   * @param {import('react').BaseSyntheticEvent} e
   */
  const handleSearchBarChange = (e) => {
    if (e.target.value === '') {
      handleSearchParamChange('search', '');
    }
    setCurrentSearch(e.target.value);
  };

  /**
   * Handle the click on the search item.
   * @param {string} email
   */
  const handleSearchItemClick = (email) => {
    const selectedCustomer = customers?.data.find(
      (customer) => customer.email === email,
    );
    handleSearchParamChange('search', selectedCustomer?.email || '');
  };

  const searchCustomerFullName = useMemo(() => {
    const fullNameFromParams = searchParam.get('customerfullname');
    if (fullNameFromParams) {
      return fullNameFromParams;
    }
    const customerParam = searchParam.get('search');
    const current = customers?.data.find(
      (customer) => customer?.email === customerParam,
    );
    if (current) {
      const fullName = getFullname(current?.firstname, current?.lastname);
      handleSearchParamChange('customerfullname', fullName);
      return fullName;
    }
    return '';
  }, [customers?.data, searchParam]);

  const brokerName = useMemo(() => {
    const brokerId = searchParam.get('brokerIds');
    const broker = admins?.data.find((admin) => admin.id === parseInt(brokerId, 10));
    return broker?.firstname ? `${broker?.firstname} ${broker?.lastname}` : '';
  }, [admins?.data, searchParam]);

  return (
    <FormSection className="full-width" label={t('customerList.filters.title')}>
      <div className="d-flex f-row f-wrap gap-s">
        <Formblock
          key="customer"
          htmlFor="customer"
          className="margin-bottom-xs"
        >
          <label htmlFor="customer" className="p2-default">
            <span>{t('customerList.filters.customer')}</span>
            <SearchBar
              placeholder={t('customerList.filters.placeholders.customer')}
              emptySearchResultLabel={t('customerList.search.noResult')}
              onChange={handleSearchBarChange}
              searchOptions={customerOptions}
              onItemClick={handleSearchItemClick}
              isSearchLoading={!customers}
              displayAvatar={false}
            />
          </label>
        </Formblock>
        <Formblock
          key="status"
          htmlFor="status"
          className="margin-bottom-xs"
        >
          <label htmlFor="status" className="p2-default">
            <span>{t('customerList.filters.status')}</span>
            <Select
              // @ts-ignore
              id="status"
              name="status"
              className="background-neutral00"
              placeholder={t('customerList.filters.placeholders.status')}
              options={CUSTOMER_STATUS}
              value={searchParam.get('status') || ''}
              onChange={(/** @type {{ target: { value: any; }; }} */ e) => handleSearchParamChange('status', e?.target?.value)}
            />
          </label>
        </Formblock>
        <Formblock
          key="brokerIds"
          htmlFor="brokerIds"
          className="margin-bottom-xs"
        >
          <label htmlFor="brokerIds" className="p2-default">
            <span>{t('customerList.filters.broker')}</span>
            <Select
              // @ts-ignore
              id="brokerIds"
              name="brokerIds"
              className="background-neutral00"
              placeholder={t('customerList.filters.placeholders.broker')}
              options={adminOptions}
              value={searchParam.get('brokerIds') || ''}
              onChange={(/** @type {{ target: { value: any; }; }} */ e) => handleSearchParamChange('brokerIds', e?.target?.value)}
            />
          </label>
        </Formblock>
      </div>
      <Box direction="row" gap="s" className="margin-bottom-xs margin-top-s">
        {customerFilter && customers?.data && (
          <Button
            icon="cross"
            onClick={() => {
              handleSearchParamChange('search', '');
              handleSearchParamChange('customerfullname', '');
            }}
            color="primary100"
            iconPosition="right"
          >
            {`${t('customerList.filters.customer')} : ${searchCustomerFullName}`}
          </Button>
        )}
        {statusFilter && (
          <Button
            icon="cross"
            onClick={() => handleSearchParamChange('status', '')}
            color="primary100"
            iconPosition="right"
          >
            {`${t('customerList.filters.status')} : ${t(`customerStatus.${statusFilter}`)}`}
          </Button>
        )}
        {brokerIds && (
          <Button
            icon="cross"
            onClick={() => handleSearchParamChange('brokerIds', '')}
            color="primary100"
            iconPosition="right"
          >
            {`${t('customerList.filters.broker')} : ${brokerName}`}
          </Button>
        )}
      </Box>
    </FormSection>
  );
}

export default CustomerFilters;
